import { memo, useEffect, useMemo, useRef, useState } from "react"

// ───────────────────────────────────────────────
// UTILS & CONFIG
// ───────────────────────────────────────────────
const clamp01 = (n) => Math.max(0, Math.min(1, n))
const slugify = (s) => (s || "").toString().trim().toLowerCase()

const getVisualZoneName = (realZoneName) => {
    if (!realZoneName) return '';
    const normalized = realZoneName.toLowerCase().trim();
    const aliases = {
        'zona1': 'Juanjolote Local',
        'zona2': 'Juanjolote Visita'
    };
    return aliases[normalized] || realZoneName;
};

const COLOR_BY_SLUG = {
  azul: "BlueAdsl", oro: "GoldenAdsl", moctezuma: "GoldenAdsl",
  nissan: "WhiteAdsl", mobil: "GoldenAdsl", tecate: "RedAdsl",
  "laboratorio tequis": "RedAdsl", "seguros el potosi": "GoldenAdsl",
  "red cola": "RedAdsl", canels: "BlueAdsl", "lounge ron potosi": "GoldenAdsl",
}

// CONFIGURACIÓN DE ALIAS (CAMUFLAJE)
// const ALIAS_BY_SLUG = {
//   canels: "Player 1",
//   moctezuma: "Player 2",
//   nissan: "Jugador 3",
//   azul: "Jugador 4",
//   oro: "Jugador 5",
//   mobil: "Jugador 6",
//   tecate: "Jugador 7",
//   "laboratorio tequis": "Jugador 8",
//   "seguros el potosi": "Jugador 9",
//   "red cola": "Player 10",
//   "lounge ron potosi": "Player 11"
// }

const getColorModifier = (zoneName) => {
  const token = COLOR_BY_SLUG[slugify(zoneName)] || "RedAdsl"
  return `--${token}`
}

// const getZoneAlias = (zoneName) => {
//     const slug = slugify(zoneName)
//     return ALIAS_BY_SLUG[slug] || zoneName 
// }

// ───────────────────────────────────────────────
// ESTADO
// ───────────────────────────────────────────────
function initZoneState() {
  return {
    tapsAccum: 0,
    filledCount: 0,
    scoreSlots: [
      { active: false, pulseKey: 0 },
      { active: false, pulseKey: 0 },
      { active: false, pulseKey: 0 },
    ],
    nextSlotIndex: 0,
    pumpBurstUntil: 0,
  }
}

const Rojito = ({ state, ui, isActiveWomensMode }) => {
  const match = state ?? { matchId: null, status: "Waiting", target: 0, zones: [], winners: null }
  const { matchId, target = 0, status = "Waiting", winners } = match
  const zonesSnap = Array.isArray(match.zones) ? match.zones : []
  const [zonesVisual, setZonesVisual] = useState({})
  const lastServerCompletedRef = useRef(new Map())

  useEffect(() => {
    if (!matchId) return
    setZonesVisual({})
    lastServerCompletedRef.current = new Map()
  }, [matchId])

  const [, forceUiTick] = useState(0)
  const scheduleUiTick = (ms) => setTimeout(() => forceUiTick(n => n + 1), ms)

  useEffect(() => {
    if (!zonesSnap.length) return
    setZonesVisual(prev => {
      let nextState = { ...prev }
      for (const z of zonesSnap) {
        const slug = slugify(z.zone)
        if (!nextState[slug]) nextState[slug] = initZoneState()
        
        const currentVisual = nextState[slug]
        const serverCompleted = Math.max(0, z.completedBalloons || 0)
        const serverRemainder = Math.max(0, z.currentBalloonTaps || 0)
        const lastSeen = lastServerCompletedRef.current.get(slug) ?? 0
        const completedDiff = serverCompleted - lastSeen
        
        let updatedZone = currentVisual

        if (completedDiff > 0) {
          let newSlots = updatedZone.scoreSlots.map(s => ({ ...s }))
          let newIdx = updatedZone.nextSlotIndex
          for (let i = 0; i < completedDiff; i++) {
            const idx = newIdx % 3
            newSlots[idx].active = true
            newSlots[idx].pulseKey = (newSlots[idx].pulseKey || 0) + 1
            newIdx = (newIdx + 1) % 3
          }
          updatedZone = {
            ...updatedZone,
            filledCount: updatedZone.filledCount + completedDiff,
            scoreSlots: newSlots,
            nextSlotIndex: newIdx,
            pumpBurstUntil: Date.now() + 250,
          }
          scheduleUiTick(260)
        }
        lastServerCompletedRef.current.set(slug, serverCompleted)

        let newTapsAccum = 0
        if (target > 0) {
          const capped = Math.min(serverRemainder, target - 1)
          newTapsAccum = Math.max(0, capped)
        }
        
        if (updatedZone.tapsAccum !== newTapsAccum || completedDiff > 0) {
            nextState = { ...nextState, [slug]: { ...updatedZone, tapsAccum: newTapsAccum } }
        }
      }
      return nextState
    })
  }, [zonesSnap, target])

  const showFinishedBanner = status === "Finished" && winners && winners.winnerType !== "none"

  return (
    <div className="Balloons__Screen__Brands--RojitoDrinks">
      
      {/* ─────────────────────────────────────────────── */}
      {/* FONDO (BACKGROUND) CONDICIONAL */}
      {/* ─────────────────────────────────────────────── */}
      <div className="Balloons__Screen__Brands--RojitoDrinks__Background">
        {isActiveWomensMode ? (
            <img 
                src='/img/games/runtogoal/background-1.png' 
                className='Balloons__Screen__Brands--RojitoDrinks__Background__Image' 
                alt="Fondo Femenil"
            />
        ) : isActiveWomensMode === false && (
            <img 
                src='/img/games/runtogoal/background-1.png' 
                className='Balloons__Screen__Brands--RojitoDrinks__Background__Image' 
                alt="Fondo Varonil"
            />
        )}
      </div>

      <p className="Balloons__Screen__Brands--RojitoDrinks__Title"></p>

      {showFinishedBanner && <WinnersBanner winners={winners} />}

      {(ui?.uiStatus === "Countdown" || ui?.uiStatus === "Running" || status === "Running") && (
        <div className="Balloons__Screen__Brands--RojitoDrinks__Players">
          {zonesSnap.map((z) => {
            const slug = slugify(z.zone)
            const visualState = zonesVisual[slug] || initZoneState()
            const rawProgress = target > 0 ? clamp01(visualState.tapsAccum / target) : 0
            const effectiveProgress = (visualState.pumpBurstUntil > Date.now()) ? 1 : rawProgress
            const colorMod = getColorModifier(z.zone)
            
            //const displayName = getZoneAlias(z.zone)
            const displayName = getVisualZoneName(z.zone)

            return (
              <PlayerColumn
                key={slug}
                displayName={displayName}
                score={visualState.filledCount}
                scoreSlots={visualState.scoreSlots}
                progress={effectiveProgress}
                colorModifier={colorMod}
              />
            )
          })}
        </div>
      )}
       <p className="Balloons__Screen__Brands--RojitoDrinks__Footer FooterCredits">Hecho con ♥ por Five Digital Minds | 2026</p>
    </div>
  )
}

// ... Resto de componentes (PlayerColumn, Box, SodaFiller, WinnersBanner) siguen igual ...
// (No es necesario repetirlos si no cambiaron, pero asegúrate de incluirlos en tu archivo)

// PARA MANTENER LA REFERENCIA COMPLETA, LOS INCLUYO ABAJO:

const PlayerColumn = ({ displayName, score, scoreSlots, progress, colorModifier }) => {
  return (
    <div className="Balloons__Screen__Brands--RojitoDrinks__Players__Player">
      <Box zoneName={displayName} counter={score} boxSlots={scoreSlots} colorMod={colorModifier} />
      <SodaFiller progress={progress} colorMod={colorModifier} />
    </div>
  )
}

function Box({ zoneName, counter, boxSlots, colorMod }) {
  return (
    <div className='Balloons__Screen__Brands--RojitoDrinks__Box'>
        <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Balloons'>
            <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Balloons__Slot'>
                {boxSlots.map((s, i) => (
                    <div
                        key={i + "-" + s.pulseKey}
                        className={[
                            "Balloons__Screen__Brands--RojitoDrinks__Box__Balloons__Slot__CanItem",
                            colorMod, 
                            s.active ? "is-visible" : "is-hidden"
                        ].join(" ")}
                    >
                        <img 
                            src="/img/games/balloons/male/january-31-2026/Interaktia-Dinamicas-Digitales-Eventos-Deportivos-Rojito-Lata-Roja-Arriba.png" 
                            alt="point" 
                        />
                    </div>
                ))}
            </div>
        </div>
        <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Box' />
        <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Counter'>
            <p className='Balloons__Screen__Brands--RojitoDrinks__Box__Counter__Title'>{zoneName.toUpperCase()}</p>
            <p className='Balloons__Screen__Brands--RojitoDrinks__Box__Counter__Text'>{counter}</p>
        </div>
    </div>
  )
}

function SodaFiller({ progress, colorMod }) {
  const cssVars = useMemo(() => ({ "--fill-level": `${progress * 100}%` }), [progress])

  return (
    <div className="Balloons__Screen__Brands--RojitoDrinks__AirPump">
      <div className="Balloons__Screen__Brands--RojitoDrinks__SodaContainer">
        <div className={`Balloons__Screen__Brands--RojitoDrinks__SodaContainer__Liquid ${colorMod}`} style={cssVars}>
           <div className="Balloons__Screen__Brands--RojitoDrinks__SodaContainer__Wave"></div>
           <div className="Balloons__Screen__Brands--RojitoDrinks__SodaContainer__Bubbles">
              <span></span><span></span><span></span>
           </div>
        </div>
        <div className="Balloons__Screen__Brands--RojitoDrinks__SodaContainer__CanOverlay">
           <img src="/img/games/balloons/male/january-31-2026/Interaktia-Dinamicas-Digitales-Eventos-Deportivos-Rojito-Lata.png" alt="Soda Can" />
        </div>
      </div>
    </div>
  )
}

function WinnersBanner({ winners }) {
  const t = winners?.winnerType
  const formatWinnerList = (list) => {
      if (!list) return ""
      //return list.map(w => getZoneAlias(w)).join(", ").toUpperCase()
      return list.map(w => getVisualZoneName(w)).join(", ").toUpperCase()
  }

  if (t === "single") {
    const rawWinner = winners?.winners?.[0] ?? ""
    //const displayName = getZoneAlias(rawWinner)
    const displayName = getVisualZoneName(rawWinner)
    return (
        <div className="Balloons__Screen__Brands--RojitoDrinks__Winners">
            <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Single'>El ganador es...</p>
            <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Single'>¡{displayName.toUpperCase()}!</p>
        </div>
    )
  }

  if (t === "tie") {
    const list = formatWinnerList(winners?.winners)
    return (
        <div className="Balloons__Screen__Brands--RojitoDrinks__Winners">
            <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Tie'>Ha habido un empate entre:</p>
            <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Tie'>{list}</p>
        </div>
    )
  }

  if (t === "all-tie") {
    return (
        <div className="Balloons__Screen__Brands--RojitoDrinks__Winners">
            <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--AllTie'>¡Han empatado todos los jugadores!</p>
        </div>
    )
  }

  return null
}

export { Rojito };










// import { memo, useEffect, useMemo, useRef, useState } from "react"

// // ───────────────────────────────────────────────
// // UTILS & CONFIG
// // ───────────────────────────────────────────────
// const clamp01 = (n) => Math.max(0, Math.min(1, n))
// const slugify = (s) => (s || "").toString().trim().toLowerCase()

// const COLOR_BY_SLUG = {
//   azul: "BlueAdsl", oro: "GoldenAdsl", moctezuma: "GoldenAdsl",
//   nissan: "WhiteAdsl", mobil: "GoldenAdsl", tecate: "RedAdsl",
//   "laboratorio tequis": "RedAdsl", "seguros el potosi": "GoldenAdsl",
//   "red cola": "RedAdsl", canels: "BlueAdsl", "lounge ron potosi": "GoldenAdsl",
// }

// // CONFIGURACIÓN DE ALIAS (CAMUFLAJE)
// const ALIAS_BY_SLUG = {
//   canels: "Player 1",
//   moctezuma: "Player 2",
//   nissan: "Jugador 3",
//   azul: "Jugador 4",
//   oro: "Jugador 5",
//   mobil: "Jugador 6",
//   tecate: "Jugador 7",
//   "laboratorio tequis": "Jugador 8",
//   "seguros el potosi": "Jugador 9",
//   "red cola": "Player 10",
//   "lounge ron potosi": "Player 11"
// }

// const getColorModifier = (zoneName) => {
//   const token = COLOR_BY_SLUG[slugify(zoneName)] || "RedAdsl"
//   return `--${token}`
// }

// const getZoneAlias = (zoneName) => {
//     const slug = slugify(zoneName)
//     return ALIAS_BY_SLUG[slug] || zoneName 
// }

// // ───────────────────────────────────────────────
// // ESTADO
// // ───────────────────────────────────────────────
// function initZoneState() {
//   return {
//     tapsAccum: 0,
//     filledCount: 0,
//     scoreSlots: [
//       { active: false, pulseKey: 0 },
//       { active: false, pulseKey: 0 },
//       { active: false, pulseKey: 0 },
//     ],
//     nextSlotIndex: 0,
//     pumpBurstUntil: 0,
//   }
// }

// const Rojito = ({ state, ui, isActiveWomensMode }) => {
//   const match = state ?? { matchId: null, status: "Waiting", target: 0, zones: [], winners: null }
//   const { matchId, target = 0, status = "Waiting", winners } = match
//   const zonesSnap = Array.isArray(match.zones) ? match.zones : []
//   const [zonesVisual, setZonesVisual] = useState({})
//   const lastServerCompletedRef = useRef(new Map())

//   useEffect(() => {
//     if (!matchId) return
//     setZonesVisual({})
//     lastServerCompletedRef.current = new Map()
//   }, [matchId])

//   const [, forceUiTick] = useState(0)
//   const scheduleUiTick = (ms) => setTimeout(() => forceUiTick(n => n + 1), ms)

//   useEffect(() => {
//     if (!zonesSnap.length) return
//     setZonesVisual(prev => {
//       let nextState = { ...prev }
//       for (const z of zonesSnap) {
//         const slug = slugify(z.zone)
//         if (!nextState[slug]) nextState[slug] = initZoneState()
        
//         const currentVisual = nextState[slug]
//         const serverCompleted = Math.max(0, z.completedBalloons || 0)
//         const serverRemainder = Math.max(0, z.currentBalloonTaps || 0)
//         const lastSeen = lastServerCompletedRef.current.get(slug) ?? 0
//         const completedDiff = serverCompleted - lastSeen
        
//         let updatedZone = currentVisual

//         if (completedDiff > 0) {
//           let newSlots = updatedZone.scoreSlots.map(s => ({ ...s }))
//           let newIdx = updatedZone.nextSlotIndex
//           for (let i = 0; i < completedDiff; i++) {
//             const idx = newIdx % 3
//             newSlots[idx].active = true
//             newSlots[idx].pulseKey = (newSlots[idx].pulseKey || 0) + 1
//             newIdx = (newIdx + 1) % 3
//           }
//           updatedZone = {
//             ...updatedZone,
//             filledCount: updatedZone.filledCount + completedDiff,
//             scoreSlots: newSlots,
//             nextSlotIndex: newIdx,
//             pumpBurstUntil: Date.now() + 250,
//           }
//           scheduleUiTick(260)
//         }
//         lastServerCompletedRef.current.set(slug, serverCompleted)

//         let newTapsAccum = 0
//         if (target > 0) {
//           const capped = Math.min(serverRemainder, target - 1)
//           newTapsAccum = Math.max(0, capped)
//         }
        
//         if (updatedZone.tapsAccum !== newTapsAccum || completedDiff > 0) {
//             nextState = { ...nextState, [slug]: { ...updatedZone, tapsAccum: newTapsAccum } }
//         }
//       }
//       return nextState
//     })
//   }, [zonesSnap, target])

//   const showFinishedBanner = status === "Finished" && winners && winners.winnerType !== "none"

//   return (
//     <div className="Balloons__Screen__Brands--RojitoDrinks">
      
//       {/* ─────────────────────────────────────────────── */}
//       {/* FONDO (BACKGROUND) CONDICIONAL */}
//       {/* ─────────────────────────────────────────────── */}
//       <div className="Balloons__Screen__Brands--RojitoDrinks__Background">
//         {isActiveWomensMode ? (
//             <img 
//                 src='/img/games/runtogoal/background-1.png' 
//                 className='Balloons__Screen__Brands--RojitoDrinks__Background__Image' 
//                 alt="Fondo Femenil"
//             />
//         ) : isActiveWomensMode === false && (
//             <img 
//                 src='/img/games/runtogoal/background-1.png' 
//                 className='Balloons__Screen__Brands--RojitoDrinks__Background__Image' 
//                 alt="Fondo Varonil"
//             />
//         )}
//       </div>

//       <p className="Balloons__Screen__Brands--RojitoDrinks__Title"></p>

//       {showFinishedBanner && <WinnersBanner winners={winners} />}

//       {(ui?.uiStatus === "Countdown" || ui?.uiStatus === "Running" || status === "Running") && (
//         <div className="Balloons__Screen__Brands--RojitoDrinks__Players">
//           {zonesSnap.map((z) => {
//             const slug = slugify(z.zone)
//             const visualState = zonesVisual[slug] || initZoneState()
//             const rawProgress = target > 0 ? clamp01(visualState.tapsAccum / target) : 0
//             const effectiveProgress = (visualState.pumpBurstUntil > Date.now()) ? 1 : rawProgress
//             const colorMod = getColorModifier(z.zone)
            
//             const displayName = getZoneAlias(z.zone)

//             return (
//               <PlayerColumn
//                 key={slug}
//                 displayName={displayName}
//                 score={visualState.filledCount}
//                 scoreSlots={visualState.scoreSlots}
//                 progress={effectiveProgress}
//                 colorModifier={colorMod}
//               />
//             )
//           })}
//         </div>
//       )}
//        <p className="Balloons__Screen__Brands--RojitoDrinks__Footer FooterCredits">Hecho con ♥ por Five Digital Minds | 2026</p>
//     </div>
//   )
// }

// // ... Resto de componentes (PlayerColumn, Box, SodaFiller, WinnersBanner) siguen igual ...
// // (No es necesario repetirlos si no cambiaron, pero asegúrate de incluirlos en tu archivo)

// // PARA MANTENER LA REFERENCIA COMPLETA, LOS INCLUYO ABAJO:

// const PlayerColumn = ({ displayName, score, scoreSlots, progress, colorModifier }) => {
//   return (
//     <div className="Balloons__Screen__Brands--RojitoDrinks__Players__Player">
//       <Box zoneName={displayName} counter={score} boxSlots={scoreSlots} colorMod={colorModifier} />
//       <SodaFiller progress={progress} colorMod={colorModifier} />
//     </div>
//   )
// }

// function Box({ zoneName, counter, boxSlots, colorMod }) {
//   return (
//     <div className='Balloons__Screen__Brands--RojitoDrinks__Box'>
//         <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Balloons'>
//             <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Balloons__Slot'>
//                 {boxSlots.map((s, i) => (
//                     <div
//                         key={i + "-" + s.pulseKey}
//                         className={[
//                             "Balloons__Screen__Brands--RojitoDrinks__Box__Balloons__Slot__CanItem",
//                             colorMod, 
//                             s.active ? "is-visible" : "is-hidden"
//                         ].join(" ")}
//                     >
//                         <img 
//                             src="/img/games/balloons/male/january-31-2026/Interaktia-Dinamicas-Digitales-Eventos-Deportivos-Rojito-Lata-Roja-Arriba.png" 
//                             alt="point" 
//                         />
//                     </div>
//                 ))}
//             </div>
//         </div>
//         <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Box' />
//         <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Counter'>
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Box__Counter__Title'>{zoneName.toUpperCase()}</p>
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Box__Counter__Text'>{counter}</p>
//         </div>
//     </div>
//   )
// }

// function SodaFiller({ progress, colorMod }) {
//   const cssVars = useMemo(() => ({ "--fill-level": `${progress * 100}%` }), [progress])

//   return (
//     <div className="Balloons__Screen__Brands--RojitoDrinks__AirPump">
//       <div className="Balloons__Screen__Brands--RojitoDrinks__SodaContainer">
//         <div className={`Balloons__Screen__Brands--RojitoDrinks__SodaContainer__Liquid ${colorMod}`} style={cssVars}>
//            <div className="Balloons__Screen__Brands--RojitoDrinks__SodaContainer__Wave"></div>
//            <div className="Balloons__Screen__Brands--RojitoDrinks__SodaContainer__Bubbles">
//               <span></span><span></span><span></span>
//            </div>
//         </div>
//         <div className="Balloons__Screen__Brands--RojitoDrinks__SodaContainer__CanOverlay">
//            <img src="/img/games/balloons/male/january-31-2026/Interaktia-Dinamicas-Digitales-Eventos-Deportivos-Rojito-Lata.png" alt="Soda Can" />
//         </div>
//       </div>
//     </div>
//   )
// }

// function WinnersBanner({ winners }) {
//   const t = winners?.winnerType
//   const formatWinnerList = (list) => {
//       if (!list) return ""
//       return list.map(w => getZoneAlias(w)).join(", ").toUpperCase()
//   }

//   if (t === "single") {
//     const rawWinner = winners?.winners?.[0] ?? ""
//     const displayName = getZoneAlias(rawWinner)
//     return (
//         <div className="Balloons__Screen__Brands--RojitoDrinks__Winners">
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Single'>El ganador es...</p>
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Single'>¡{displayName.toUpperCase()}!</p>
//         </div>
//     )
//   }
//   if (t === "tie") {
//     const list = formatWinnerList(winners?.winners)
//     return (
//         <div className="Balloons__Screen__Brands--RojitoDrinks__Winners">
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Tie'>Ha habido un empate entre:</p>
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Tie'>{list}</p>
//         </div>
//     )
//   }
//   if (t === "all-tie") {
//     return (
//         <div className="Balloons__Screen__Brands--RojitoDrinks__Winners">
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--AllTie'>¡Han empatado todos los jugadores!</p>
//         </div>
//     )
//   }
//   return null
// }

// export { Rojito };









// Funcional sin fondo
// import { memo, useEffect, useMemo, useRef, useState } from "react"

// // ───────────────────────────────────────────────
// // UTILS & CONFIG
// // ───────────────────────────────────────────────
// const clamp01 = (n) => Math.max(0, Math.min(1, n))
// const slugify = (s) => (s || "").toString().trim().toLowerCase()

// const COLOR_BY_SLUG = {
//   azul: "BlueAdsl", oro: "GoldenAdsl", moctezuma: "GoldenAdsl",
//   nissan: "WhiteAdsl", mobil: "GoldenAdsl", tecate: "RedAdsl",
//   "laboratorio tequis": "RedAdsl", "seguros el potosi": "GoldenAdsl",
//   "red cola": "RedAdsl", canels: "BlueAdsl", "lounge ron potosi": "GoldenAdsl",
// }

// // 1. CONFIGURACIÓN DE ALIAS (CAMUFLAJE)
// // Aquí defines qué nombre mostrar en pantalla para cada zona
// const ALIAS_BY_SLUG = {
//   canels: "Player 1",
//   moctezuma: "Player 2",
//   nissan: "Jugador 3",
//   // Agregué el resto para que no se vean raros si aparecen
//   azul: "Jugador 4",
//   oro: "Jugador 5",
//   mobil: "Jugador 6",
//   tecate: "Jugador 7",
//   "laboratorio tequis": "Jugador 8",
//   "seguros el potosi": "Jugador 9",
//   "red cola": "Player 10",
//   "lounge ron potosi": "Player 11"
// }

// const getColorModifier = (zoneName) => {
//   const token = COLOR_BY_SLUG[slugify(zoneName)] || "RedAdsl"
//   return `--${token}`
// }

// // Helper para obtener el nombre camuflajeado
// const getZoneAlias = (zoneName) => {
//     const slug = slugify(zoneName)
//     // Si existe en el mapa, devuelve el alias (Jugador X), si no, devuelve el nombre original
//     return ALIAS_BY_SLUG[slug] || zoneName 
// }

// // ───────────────────────────────────────────────
// // ESTADO
// // ───────────────────────────────────────────────
// function initZoneState() {
//   return {
//     tapsAccum: 0,
//     filledCount: 0,
//     scoreSlots: [
//       { active: false, pulseKey: 0 },
//       { active: false, pulseKey: 0 },
//       { active: false, pulseKey: 0 },
//     ],
//     nextSlotIndex: 0,
//     pumpBurstUntil: 0,
//   }
// }

// const Rojito = ({ state, ui, isActiveWomensMode }) => {
//   const match = state ?? { matchId: null, status: "Waiting", target: 0, zones: [], winners: null }
//   const { matchId, target = 0, status = "Waiting", winners } = match
//   const zonesSnap = Array.isArray(match.zones) ? match.zones : []
//   const [zonesVisual, setZonesVisual] = useState({})
//   const lastServerCompletedRef = useRef(new Map())

//   useEffect(() => {
//     if (!matchId) return
//     setZonesVisual({})
//     lastServerCompletedRef.current = new Map()
//   }, [matchId])

//   const [, forceUiTick] = useState(0)
//   const scheduleUiTick = (ms) => setTimeout(() => forceUiTick(n => n + 1), ms)

//   useEffect(() => {
//     if (!zonesSnap.length) return
//     setZonesVisual(prev => {
//       let nextState = { ...prev }
//       for (const z of zonesSnap) {
//         const slug = slugify(z.zone)
//         if (!nextState[slug]) nextState[slug] = initZoneState()
        
//         const currentVisual = nextState[slug]
//         const serverCompleted = Math.max(0, z.completedBalloons || 0)
//         const serverRemainder = Math.max(0, z.currentBalloonTaps || 0)
//         const lastSeen = lastServerCompletedRef.current.get(slug) ?? 0
//         const completedDiff = serverCompleted - lastSeen
        
//         let updatedZone = currentVisual

//         if (completedDiff > 0) {
//           let newSlots = updatedZone.scoreSlots.map(s => ({ ...s }))
//           let newIdx = updatedZone.nextSlotIndex
//           for (let i = 0; i < completedDiff; i++) {
//             const idx = newIdx % 3
//             newSlots[idx].active = true
//             newSlots[idx].pulseKey = (newSlots[idx].pulseKey || 0) + 1
//             newIdx = (newIdx + 1) % 3
//           }
//           updatedZone = {
//             ...updatedZone,
//             filledCount: updatedZone.filledCount + completedDiff,
//             scoreSlots: newSlots,
//             nextSlotIndex: newIdx,
//             pumpBurstUntil: Date.now() + 250,
//           }
//           scheduleUiTick(260)
//         }
//         lastServerCompletedRef.current.set(slug, serverCompleted)

//         let newTapsAccum = 0
//         if (target > 0) {
//           const capped = Math.min(serverRemainder, target - 1)
//           newTapsAccum = Math.max(0, capped)
//         }
        
//         if (updatedZone.tapsAccum !== newTapsAccum || completedDiff > 0) {
//             nextState = { ...nextState, [slug]: { ...updatedZone, tapsAccum: newTapsAccum } }
//         }
//       }
//       return nextState
//     })
//   }, [zonesSnap, target])

//   const showFinishedBanner = status === "Finished" && winners && winners.winnerType !== "none"

//   return (
//     <div className="Balloons__Screen__Brands--RojitoDrinks">
//       <p className="Balloons__Screen__Brands--RojitoDrinks__Title"></p>

//       {showFinishedBanner && <WinnersBanner winners={winners} />}

//       {(ui?.uiStatus === "Countdown" || ui?.uiStatus === "Running" || status === "Running") && (
//         <div className="Balloons__Screen__Brands--RojitoDrinks__Players">
//           {zonesSnap.map((z) => {
//             const slug = slugify(z.zone)
//             const visualState = zonesVisual[slug] || initZoneState()
//             const rawProgress = target > 0 ? clamp01(visualState.tapsAccum / target) : 0
//             const effectiveProgress = (visualState.pumpBurstUntil > Date.now()) ? 1 : rawProgress
//             const colorMod = getColorModifier(z.zone)
            
//             // Obtenemos el Alias aquí
//             const displayName = getZoneAlias(z.zone)

//             return (
//               <PlayerColumn
//                 key={slug}
//                 displayName={displayName} // Pasamos el Alias
//                 score={visualState.filledCount}
//                 scoreSlots={visualState.scoreSlots}
//                 progress={effectiveProgress}
//                 colorModifier={colorMod}
//               />
//             )
//           })}
//         </div>
//       )}
//        <p className="Balloons__Screen__Brands--RojitoDrinks__Footer FooterCredits">Hecho con ♥ por Five Digital Minds | 2026</p>
//     </div>
//   )
// }

// // ───────────────────────────────────────────────
// // COMPONENTES DE PRESENTACIÓN
// // ───────────────────────────────────────────────

// const PlayerColumn = ({ displayName, score, scoreSlots, progress, colorModifier }) => {
//   return (
//     <div className="Balloons__Screen__Brands--RojitoDrinks__Players__Player">
//       <Box zoneName={displayName} counter={score} boxSlots={scoreSlots} colorMod={colorModifier} />
//       <SodaFiller progress={progress} colorMod={colorModifier} />
//     </div>
//   )
// }

// function Box({ zoneName, counter, boxSlots, colorMod }) {
//   return (
//     <div className='Balloons__Screen__Brands--RojitoDrinks__Box'>
//         <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Balloons'>
//             <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Balloons__Slot'>
//                 {boxSlots.map((s, i) => (
//                     <div
//                         key={i + "-" + s.pulseKey}
//                         className={[
//                             "Balloons__Screen__Brands--RojitoDrinks__Box__Balloons__Slot__CanItem",
//                             colorMod, 
//                             s.active ? "is-visible" : "is-hidden"
//                         ].join(" ")}
//                     >
//                         {/* IMAGEN DE LA LATITA (Arriba) */}
//                         <img 
//                             src="/img/games/balloons/male/january-31-2026/Interaktia-Dinamicas-Digitales-Eventos-Deportivos-Rojito-Lata-Roja-Arriba.png" 
//                             alt="point" 
//                         />
//                     </div>
//                 ))}
//             </div>
//         </div>
//         <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Box' />
//         <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Counter'>
//             {/* Aquí renderizamos el ALIAS en mayúsculas */}
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Box__Counter__Title'>{zoneName.toUpperCase()}</p>
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Box__Counter__Text'>{counter}</p>
//         </div>
//     </div>
//   )
// }

// function SodaFiller({ progress, colorMod }) {
//   const cssVars = useMemo(() => ({ "--fill-level": `${progress * 100}%` }), [progress])

//   return (
//     <div className="Balloons__Screen__Brands--RojitoDrinks__AirPump">
//       <div className="Balloons__Screen__Brands--RojitoDrinks__SodaContainer">
        
//         {/* LÍQUIDO (Fondo) con Burbujas restauradas */}
//         <div className={`Balloons__Screen__Brands--RojitoDrinks__SodaContainer__Liquid ${colorMod}`} style={cssVars}>
//            {/* La ola blanca encima del líquido */}
//            <div className="Balloons__Screen__Brands--RojitoDrinks__SodaContainer__Wave"></div>
           
//            {/* ¡BURBUJAS VOLVIERON! */}
//            <div className="Balloons__Screen__Brands--RojitoDrinks__SodaContainer__Bubbles">
//               <span></span><span></span><span></span>
//            </div>
//         </div>

//         {/* LATA (Frente) */}
//         <div className="Balloons__Screen__Brands--RojitoDrinks__SodaContainer__CanOverlay">
//            <img src="/img/games/balloons/male/january-31-2026/Interaktia-Dinamicas-Digitales-Eventos-Deportivos-Rojito-Lata.png" alt="Soda Can" />
//         </div>

//       </div>
//     </div>
//   )
// }

// function WinnersBanner({ winners }) {
//   const t = winners?.winnerType

//   // Helper local para formatear lista de ganadores usando el ALIAS
//   const formatWinnerList = (list) => {
//       if (!list) return ""
//       // Mapeamos cada ganador a su Alias antes de hacer el join
//       return list.map(w => getZoneAlias(w)).join(", ").toUpperCase()
//   }

//   if (t === "single") {
//     const rawWinner = winners?.winners?.[0] ?? ""
//     const displayName = getZoneAlias(rawWinner) // Alias para single

//     return (
//         <div className="Balloons__Screen__Brands--RojitoDrinks__Winners">
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Single'>La zona ganadora es...</p>
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Single'>¡{displayName.toUpperCase()}!</p>
//         </div>
//     )
//   }
//   if (t === "tie") {
//     const list = formatWinnerList(winners?.winners) // Alias para empates
//     return (
//         <div className="Balloons__Screen__Brands--RojitoDrinks__Winners">
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Tie'>Ha habido un empate entre las zonas</p>
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Tie'>{list}</p>
//         </div>
//     )
//   }
//   if (t === "all-tie") {
//     return (
//         <div className="Balloons__Screen__Brands--RojitoDrinks__Winners">
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--AllTie'>¡Han empatado todas las zonas!</p>
//         </div>
//     )
//   }
//   return null
// }

// export { Rojito };









// import { memo, useEffect, useMemo, useRef, useState } from "react"

// // ───────────────────────────────────────────────
// // UTILS & CONFIG
// // ───────────────────────────────────────────────
// const clamp01 = (n) => Math.max(0, Math.min(1, n))
// const slugify = (s) => (s || "").toString().trim().toLowerCase()

// const COLOR_BY_SLUG = {
//   azul: "BlueAdsl", oro: "GoldenAdsl", moctezuma: "GoldenAdsl",
//   nissan: "WhiteAdsl", mobil: "GoldenAdsl", tecate: "RedAdsl",
//   "laboratorio tequis": "RedAdsl", "seguros el potosi": "GoldenAdsl",
//   "red cola": "RedAdsl", canels: "BlueAdsl", "lounge ron potosi": "GoldenAdsl",
// }

// const getColorModifier = (zoneName) => {
//   const token = COLOR_BY_SLUG[slugify(zoneName)] || "RedAdsl"
//   return `--${token}`
// }

// // ───────────────────────────────────────────────
// // ESTADO
// // ───────────────────────────────────────────────
// function initZoneState() {
//   return {
//     tapsAccum: 0,
//     filledCount: 0,
//     scoreSlots: [
//       { active: false, pulseKey: 0 },
//       { active: false, pulseKey: 0 },
//       { active: false, pulseKey: 0 },
//     ],
//     nextSlotIndex: 0,
//     pumpBurstUntil: 0,
//   }
// }

// const Rojito = ({ state, ui }) => {
//   const match = state ?? { matchId: null, status: "Waiting", target: 0, zones: [], winners: null }
//   const { matchId, target = 0, status = "Waiting", winners } = match
//   const zonesSnap = Array.isArray(match.zones) ? match.zones : []
//   const [zonesVisual, setZonesVisual] = useState({})
//   const lastServerCompletedRef = useRef(new Map())

//   useEffect(() => {
//     if (!matchId) return
//     setZonesVisual({})
//     lastServerCompletedRef.current = new Map()
//   }, [matchId])

//   const [, forceUiTick] = useState(0)
//   const scheduleUiTick = (ms) => setTimeout(() => forceUiTick(n => n + 1), ms)

//   useEffect(() => {
//     if (!zonesSnap.length) return
//     setZonesVisual(prev => {
//       let nextState = { ...prev }
//       for (const z of zonesSnap) {
//         const slug = slugify(z.zone)
//         if (!nextState[slug]) nextState[slug] = initZoneState()
        
//         const currentVisual = nextState[slug]
//         const serverCompleted = Math.max(0, z.completedBalloons || 0)
//         const serverRemainder = Math.max(0, z.currentBalloonTaps || 0)
//         const lastSeen = lastServerCompletedRef.current.get(slug) ?? 0
//         const completedDiff = serverCompleted - lastSeen
        
//         let updatedZone = currentVisual

//         if (completedDiff > 0) {
//           let newSlots = updatedZone.scoreSlots.map(s => ({ ...s }))
//           let newIdx = updatedZone.nextSlotIndex
//           for (let i = 0; i < completedDiff; i++) {
//             const idx = newIdx % 3
//             newSlots[idx].active = true
//             newSlots[idx].pulseKey = (newSlots[idx].pulseKey || 0) + 1
//             newIdx = (newIdx + 1) % 3
//           }
//           updatedZone = {
//             ...updatedZone,
//             filledCount: updatedZone.filledCount + completedDiff,
//             scoreSlots: newSlots,
//             nextSlotIndex: newIdx,
//             pumpBurstUntil: Date.now() + 250,
//           }
//           scheduleUiTick(260)
//         }
//         lastServerCompletedRef.current.set(slug, serverCompleted)

//         let newTapsAccum = 0
//         if (target > 0) {
//           const capped = Math.min(serverRemainder, target - 1)
//           newTapsAccum = Math.max(0, capped)
//         }
        
//         if (updatedZone.tapsAccum !== newTapsAccum || completedDiff > 0) {
//             nextState = { ...nextState, [slug]: { ...updatedZone, tapsAccum: newTapsAccum } }
//         }
//       }
//       return nextState
//     })
//   }, [zonesSnap, target])

//   const showFinishedBanner = status === "Finished" && winners && winners.winnerType !== "none"

//   return (
//     <div className="Balloons__Screen__Brands--RojitoDrinks">
//       <p className="Balloons__Screen__Brands--RojitoDrinks__Title"></p>

//       {showFinishedBanner && <WinnersBanner winners={winners} />}

//       {(ui?.uiStatus === "Countdown" || ui?.uiStatus === "Running" || status === "Running") && (
//         <div className="Balloons__Screen__Brands--RojitoDrinks__Players">
//           {zonesSnap.map((z) => {
//             const slug = slugify(z.zone)
//             const visualState = zonesVisual[slug] || initZoneState()
//             const rawProgress = target > 0 ? clamp01(visualState.tapsAccum / target) : 0
//             const effectiveProgress = (visualState.pumpBurstUntil > Date.now()) ? 1 : rawProgress
//             const colorMod = getColorModifier(z.zone)

//             return (
//               <PlayerColumn
//                 key={slug}
//                 zoneName={z.zone}
//                 score={visualState.filledCount}
//                 scoreSlots={visualState.scoreSlots}
//                 progress={effectiveProgress}
//                 colorModifier={colorMod}
//               />
//             )
//           })}
//         </div>
//       )}
//        <p className="Balloons__Screen__Brands--RojitoDrinks__Footer FooterCredits">Hecho con ♥ por Five Digital Minds | 2026</p>
//     </div>
//   )
// }

// // ───────────────────────────────────────────────
// // COMPONENTES DE PRESENTACIÓN
// // ───────────────────────────────────────────────

// const PlayerColumn = ({ zoneName, score, scoreSlots, progress, colorModifier }) => {
//   return (
//     <div className="Balloons__Screen__Brands--RojitoDrinks__Players__Player">
//       <Box zoneName={zoneName} counter={score} boxSlots={scoreSlots} colorMod={colorModifier} />
//       <SodaFiller progress={progress} colorMod={colorModifier} />
//     </div>
//   )
// }

// function Box({ zoneName, counter, boxSlots, colorMod }) {
//   return (
//     <div className='Balloons__Screen__Brands--RojitoDrinks__Box'>
//         <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Balloons'>
//             <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Balloons__Slot'>
//                 {boxSlots.map((s, i) => (
//                     <div
//                         key={i + "-" + s.pulseKey}
//                         className={[
//                             "Balloons__Screen__Brands--RojitoDrinks__Box__Balloons__Slot__CanItem",
//                             colorMod, 
//                             s.active ? "is-visible" : "is-hidden"
//                         ].join(" ")}
//                     >
//                         {/* IMAGEN DE LA LATITA (Misma que abajo, reusada) */}
//                         <img 
//                             src="/img/games/balloons/male/january-31-2026/Interaktia-Dinamicas-Digitales-Eventos-Deportivos-Rojito-Lata-Roja-Arriba.png" 
//                             alt="point" 
//                         />
//                     </div>
//                 ))}
//             </div>
//         </div>
//         <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Box' />
//         <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Counter'>
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Box__Counter__Text'>{zoneName.toUpperCase()}</p>
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Box__Counter__Text'>{counter}</p>
//         </div>
//     </div>
//   )
// }

// function SodaFiller({ progress, colorMod }) {
//   const cssVars = useMemo(() => ({ "--fill-level": `${progress * 100}%` }), [progress])

//   return (
//     <div className="Balloons__Screen__Brands--RojitoDrinks__AirPump">
//       <div className="Balloons__Screen__Brands--RojitoDrinks__SodaContainer">
        
//         {/* LÍQUIDO (Fondo) con Burbujas restauradas */}
//         <div className={`Balloons__Screen__Brands--RojitoDrinks__SodaContainer__Liquid ${colorMod}`} style={cssVars}>
//            {/* La ola blanca encima del líquido */}
//            <div className="Balloons__Screen__Brands--RojitoDrinks__SodaContainer__Wave"></div>
           
//            {/* ¡BURBUJAS VOLVIERON! */}
//            <div className="Balloons__Screen__Brands--RojitoDrinks__SodaContainer__Bubbles">
//               <span></span><span></span><span></span>
//            </div>
//         </div>

//         {/* LATA (Frente) */}
//         <div className="Balloons__Screen__Brands--RojitoDrinks__SodaContainer__CanOverlay">
//            <img src="/img/games/balloons/male/january-31-2026/Interaktia-Dinamicas-Digitales-Eventos-Deportivos-Rojito-Lata.png" alt="Soda Can" />
//         </div>

//       </div>
//     </div>
//   )
// }

// function WinnersBanner({ winners }) {
//   const t = winners?.winnerType
//   if (t === "single") {
//     const z = winners?.winners?.[0] ?? ""
//     return (
//         <div className="Balloons__Screen__Brands--RojitoDrinks__Winners">
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Single'>La zona ganadora es...</p>
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Single'>¡{z.toUpperCase()}!</p>
//         </div>
//     )
//   }
//   if (t === "tie") {
//     const list = winners?.winners?.toUpperCase().join(", ") ?? ""
//     return (
//         <div className="Balloons__Screen__Brands--RojitoDrinks__Winners">
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Tie'>Ha habido un empate entre las zonas</p>
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Tie'>{list}</p>
//         </div>
//     )
//   }
//   if (t === "all-tie") {
//     return (
//         <div className="Balloons__Screen__Brands--RojitoDrinks__Winners">
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--AllTie'>¡Han empatado todas las zonas!</p>
//         </div>
//     )
//   }
//   return null
// }

// export { Rojito };









// import { memo, useEffect, useMemo, useRef, useState } from "react"

// // ───────────────────────────────────────────────
// // UTILS & CONFIG (Igual que antes)
// // ───────────────────────────────────────────────
// const clamp01 = (n) => Math.max(0, Math.min(1, n))
// const slugify = (s) => (s || "").toString().trim().toLowerCase()

// const COLOR_BY_SLUG = {
//   azul: "BlueAdsl", oro: "GoldenAdsl", moctezuma: "GoldenAdsl",
//   nissan: "WhiteAdsl", mobil: "GoldenAdsl", tecate: "RedAdsl",
//   "laboratorio tequis": "RedAdsl", "seguros el potosi": "GoldenAdsl",
//   "red cola": "RedAdsl", canels: "BlueAdsl", "lounge ron potosi": "GoldenAdsl",
// }

// const getColorModifier = (zoneName) => {
//   const token = COLOR_BY_SLUG[slugify(zoneName)] || "RedAdsl"
//   return `--${token}`
// }

// // ───────────────────────────────────────────────
// // ESTADO Y LÓGICA (Sin cambios, funciona bien)
// // ───────────────────────────────────────────────
// function initZoneState() {
//   return {
//     tapsAccum: 0,
//     filledCount: 0,
//     scoreSlots: [
//       { active: false, pulseKey: 0 },
//       { active: false, pulseKey: 0 },
//       { active: false, pulseKey: 0 },
//     ],
//     nextSlotIndex: 0,
//     pumpBurstUntil: 0,
//   }
// }

// const Rojito = ({ state, ui }) => {
//   const match = state ?? { matchId: null, status: "Waiting", target: 0, zones: [], winners: null }
//   const { matchId, target = 0, status = "Waiting", winners } = match
//   const zonesSnap = Array.isArray(match.zones) ? match.zones : []
//   const [zonesVisual, setZonesVisual] = useState({})
//   const lastServerCompletedRef = useRef(new Map())

//   useEffect(() => {
//     if (!matchId) return
//     setZonesVisual({})
//     lastServerCompletedRef.current = new Map()
//   }, [matchId])

//   const [, forceUiTick] = useState(0)
//   const scheduleUiTick = (ms) => setTimeout(() => forceUiTick(n => n + 1), ms)

//   useEffect(() => {
//     if (!zonesSnap.length) return
//     setZonesVisual(prev => {
//       let nextState = { ...prev }
//       for (const z of zonesSnap) {
//         const slug = slugify(z.zone)
//         if (!nextState[slug]) nextState[slug] = initZoneState()
        
//         const currentVisual = nextState[slug]
//         const serverCompleted = Math.max(0, z.completedBalloons || 0)
//         const serverRemainder = Math.max(0, z.currentBalloonTaps || 0)
//         const lastSeen = lastServerCompletedRef.current.get(slug) ?? 0
//         const completedDiff = serverCompleted - lastSeen
        
//         let updatedZone = currentVisual

//         if (completedDiff > 0) {
//           let newSlots = updatedZone.scoreSlots.map(s => ({ ...s }))
//           let newIdx = updatedZone.nextSlotIndex
//           for (let i = 0; i < completedDiff; i++) {
//             const idx = newIdx % 3
//             newSlots[idx].active = true
//             newSlots[idx].pulseKey = (newSlots[idx].pulseKey || 0) + 1
//             newIdx = (newIdx + 1) % 3
//           }
//           updatedZone = {
//             ...updatedZone,
//             filledCount: updatedZone.filledCount + completedDiff,
//             scoreSlots: newSlots,
//             nextSlotIndex: newIdx,
//             pumpBurstUntil: Date.now() + 250,
//           }
//           scheduleUiTick(260)
//         }
//         lastServerCompletedRef.current.set(slug, serverCompleted)

//         let newTapsAccum = 0
//         if (target > 0) {
//           const capped = Math.min(serverRemainder, target - 1)
//           newTapsAccum = Math.max(0, capped)
//         }
        
//         if (updatedZone.tapsAccum !== newTapsAccum || completedDiff > 0) {
//             nextState = { ...nextState, [slug]: { ...updatedZone, tapsAccum: newTapsAccum } }
//         }
//       }
//       return nextState
//     })
//   }, [zonesSnap, target])

//   const showFinishedBanner = status === "Finished" && winners && winners.winnerType !== "none"

//   return (
//     <div className="Balloons__Screen__Brands--RojitoDrinks">
//       <p className="Balloons__Screen__Brands--RojitoDrinks__Title"></p>

//       {showFinishedBanner && <WinnersBanner winners={winners} />}

//       {(ui?.uiStatus === "Countdown" || ui?.uiStatus === "Running" || status === "Running") && (
//         <div className="Balloons__Screen__Brands--RojitoDrinks__Players">
//           {zonesSnap.map((z) => {
//             const slug = slugify(z.zone)
//             const visualState = zonesVisual[slug] || initZoneState()
//             const rawProgress = target > 0 ? clamp01(visualState.tapsAccum / target) : 0
//             const effectiveProgress = (visualState.pumpBurstUntil > Date.now()) ? 1 : rawProgress
//             const colorMod = getColorModifier(z.zone)

//             return (
//               <PlayerColumn
//                 key={slug}
//                 zoneName={z.zone}
//                 score={visualState.filledCount}
//                 scoreSlots={visualState.scoreSlots}
//                 progress={effectiveProgress}
//                 colorModifier={colorMod}
//               />
//             )
//           })}
//         </div>
//       )}
//        <p className="Balloons__Screen__Brands--RojitoDrinks__Footer FooterCredits">Hecho con ♥ por Five Digital Minds | 2026</p>
//     </div>
//   )
// }

// // ───────────────────────────────────────────────
// // COMPONENTES VISUALES
// // ───────────────────────────────────────────────

// const PlayerColumn = ({ zoneName, score, scoreSlots, progress, colorModifier }) => {
//   return (
//     <div className="Balloons__Screen__Brands--RojitoDrinks__Players__Player">
//       {/* Marcador Superior (Estilo Original Box) */}
//       <Box zoneName={zoneName} counter={score} boxSlots={scoreSlots} colorMod={colorModifier} />
      
//       {/* Lata Inferior (Nuevo Estilo Líquido) */}
//       <SodaFiller progress={progress} colorMod={colorModifier} />
//     </div>
//   )
// }

// /**
//  * Componente BOX restaurado a su estructura original:
//  * Slots arriba -> Barra Gris -> Contador Texto
//  */
// function Box({ zoneName, counter, boxSlots, colorMod }) {
//   return (
//     <div className='Balloons__Screen__Brands--RojitoDrinks__Box'>
//         <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Balloons'>
//             <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Balloons__Slot'>
//                 {boxSlots.map((s, i) => (
//                     <div
//                         key={i + "-" + s.pulseKey}
//                         // Usamos una clase nueva __CanItem para diferenciar del globo viejo
//                         className={[
//                             "Balloons__Screen__Brands--RojitoDrinks__Box__Balloons__Slot__CanItem",
//                             colorMod, // --RedAdsl, etc
//                             s.active ? "is-visible" : "is-hidden"
//                         ].join(" ")}
//                     />
//                 ))}
//             </div>
//         </div>
//         {/* La barra gris original */}
//         <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Box' />
        
//         {/* El contador de texto original */}
//         <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Counter'>
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Box__Counter__Text'>{zoneName.toUpperCase()}</p>
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Box__Counter__Text'>{counter}</p>
//         </div>
//     </div>
//   )
// }

// function SodaFiller({ progress, colorMod }) {
//   const cssVars = useMemo(() => ({ "--fill-level": `${progress * 100}%` }), [progress])

//   return (
//     <div className="Balloons__Screen__Brands--RojitoDrinks__AirPump"> {/* Mantenemos clase contenedora para respetar layouts */}
//       <div className="Balloons__Screen__Brands--RojitoDrinks__SodaContainer">
        
//         {/* Líquido de Fondo */}
//         <div className={`Balloons__Screen__Brands--RojitoDrinks__SodaContainer__Liquid ${colorMod}`} style={cssVars}>
//            {/* Ola (opcional, CSS puro) */}
//            <div className="Balloons__Screen__Brands--RojitoDrinks__SodaContainer__Wave"></div>
//         </div>

//         {/* Imagen de la Lata (Frente) */}
//         <div className="Balloons__Screen__Brands--RojitoDrinks__SodaContainer__CanOverlay">
//            <img src="/img/games/balloons/male/january-31-2026/Interaktia-Dinamicas-Digitales-Eventos-Deportivos-Rojito-Lata.png" alt="Soda Can" />
//         </div>

//       </div>
//     </div>
//   )
// }

// // ───────────────────────────────────────────────
// // BANNER GANADORES (Tu código original intacto)
// // ───────────────────────────────────────────────
// function WinnersBanner({ winners }) {
//   const t = winners?.winnerType
//   if (t === "single") {
//     const z = winners?.winners?.[0] ?? ""
//     return (
//         <div className="Balloons__Screen__Brands--RojitoDrinks__Winners">
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Single'>La zona ganadora es...</p>
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Single'>¡{z.toUpperCase()}!</p>
//         </div>
//     )
//   }
//   if (t === "tie") {
//     const list = winners?.winners?.toUpperCase().join(", ") ?? ""
//     return (
//         <div className="Balloons__Screen__Brands--RojitoDrinks__Winners">
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Tie'>Ha habido un empate entre las zonas</p>
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Tie'>{list}</p>
//         </div>
//     )
//   }
//   if (t === "all-tie") {
//     return (
//         <div className="Balloons__Screen__Brands--RojitoDrinks__Winners">
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--AllTie'>¡Han empatado todas las zonas!</p>
//         </div>
//     )
//   }
//   return null
// }

// export { Rojito };









// import { memo, useEffect, useMemo, useRef, useState } from "react"

// // ───────────────────────────────────────────────
// // UTILERÍAS Y CONFIGURACIÓN
// // ───────────────────────────────────────────────
// const clamp01 = (n) => Math.max(0, Math.min(1, n))
// const slugify = (s) => (s || "").toString().trim().toLowerCase()

// // Diccionario de colores por zona (Ajustar según tus assets reales)
// const COLOR_BY_SLUG = {
//   azul: "BlueAdsl", oro: "GoldenAdsl", moctezuma: "GoldenAdsl",
//   nissan: "WhiteAdsl", mobil: "GoldenAdsl", tecate: "RedAdsl",
//   "laboratorio tequis": "RedAdsl", "seguros el potosi": "GoldenAdsl",
//   "red cola": "RedAdsl", canels: "BlueAdsl", "lounge ron potosi": "GoldenAdsl",
// }

// // Devuelve el modificador BEM para el color según la zona
// const getColorModifier = (zoneName) => {
//   const token = COLOR_BY_SLUG[slugify(zoneName)] || "RedAdsl"
//   return `--${token}` // Ej: --RedAdsl
// }

// // ───────────────────────────────────────────────
// // ESTADO INICIAL (LÓGICA DE NEGOCIO)
// // ───────────────────────────────────────────────
// function initZoneState() {
//   return {
//     tapsAccum: 0,         // Taps acumulados para la lata actual (progreso 0-1)
//     filledCount: 0,       // Latas totalmente llenas (Score)
//     // Array de slots para el marcador superior (3 espacios visibles)
//     scoreSlots: [
//       { active: false, pulseKey: 0 },
//       { active: false, pulseKey: 0 },
//       { active: false, pulseKey: 0 },
//     ],
//     nextSlotIndex: 0,     // Puntero para el carrusel de slots
//     pumpBurstUntil: 0,    // Timestamp para forzar visualización "llena" tras completar
//   }
// }

// // ───────────────────────────────────────────────
// // COMPONENTE PRINCIPAL
// // ───────────────────────────────────────────────
// const Rojito = ({ state, ui }) => {
//   // 1. Extracción de datos del Snapshot del Servidor
//   const match = state ?? { matchId: null, status: "Waiting", target: 0, zones: [], winners: null }
//   const { matchId, target = 0, status = "Waiting", winners } = match
//   const zonesSnap = Array.isArray(match.zones) ? match.zones : []

//   // 2. Estado Visual Local (Independiente del render del servidor para suavidad)
//   const [zonesVisual, setZonesVisual] = useState({})
//   const lastServerCompletedRef = useRef(new Map()) // Evita re-procesar eventos ya animados

//   // Reset al cambiar de partida
//   useEffect(() => {
//     if (!matchId) return
//     setZonesVisual({})
//     lastServerCompletedRef.current = new Map()
//   }, [matchId])

//   // "Ticking" para limpiar efectos temporales (como el burst de llenado)
//   const [, forceUiTick] = useState(0)
//   const scheduleUiTick = (ms) => setTimeout(() => forceUiTick(n => n + 1), ms)

//   // 3. Lógica de Reconciliación (Core del Juego)
//   useEffect(() => {
//     if (!zonesSnap.length) return

//     setZonesVisual(prev => {
//       let nextState = { ...prev }

//       for (const z of zonesSnap) {
//         const slug = slugify(z.zone)
//         // Inicializa zona si no existe
//         if (!nextState[slug]) nextState[slug] = initZoneState()
        
//         const currentVisual = nextState[slug]
//         const serverCompleted = Math.max(0, z.completedBalloons || 0) // Backend dice "completedBalloons", nosotros entendemos "Latas"
//         const serverRemainder = Math.max(0, z.currentBalloonTaps || 0)

//         // Detectar cambio en "Completados" (Latas llenas)
//         const lastSeen = lastServerCompletedRef.current.get(slug) ?? 0
//         const completedDiff = serverCompleted - lastSeen
        
//         let updatedZone = currentVisual

//         // Si se completaron latas nuevas, actualizamos el marcador superior
//         if (completedDiff > 0) {
//           let newSlots = updatedZone.scoreSlots.map(s => ({ ...s }))
//           let newIdx = updatedZone.nextSlotIndex
          
//           // Actualizamos los slots en carrusel
//           for (let i = 0; i < completedDiff; i++) {
//             const idx = newIdx % 3
//             newSlots[idx].active = true
//             newSlots[idx].pulseKey = (newSlots[idx].pulseKey || 0) + 1 // Fuerza re-animación CSS
//             newIdx = (newIdx + 1) % 3
//           }

//           updatedZone = {
//             ...updatedZone,
//             filledCount: updatedZone.filledCount + completedDiff,
//             scoreSlots: newSlots,
//             nextSlotIndex: newIdx,
//             pumpBurstUntil: Date.now() + 250, // Mantiene la lata llena visualmente 250ms antes de resetear
//           }
//           scheduleUiTick(260) // Programa limpieza del burst
//         }

//         // Guardar referencia para no repetir
//         lastServerCompletedRef.current.set(slug, serverCompleted)

//         // Calcular Progreso de la lata actual
//         let newTapsAccum = 0
//         if (target > 0) {
//           const capped = Math.min(serverRemainder, target - 1)
//           newTapsAccum = Math.max(0, capped)
//         }
        
//         // Sincronización final de estado
//         if (updatedZone.tapsAccum !== newTapsAccum || completedDiff > 0) {
//             // Si hubo cambio visual, actualizamos
//             nextState = { ...nextState, [slug]: { ...updatedZone, tapsAccum: newTapsAccum } }
//         }
//       }
//       return nextState
//     })
//   }, [zonesSnap, target])

//   const showFinishedBanner = status === "Finished" && winners && winners.winnerType !== "none"

//   return (
//     <div className="RojitoGame">
//       {/* Banner de Ganadores */}
//       {showFinishedBanner && <WinnersBanner winners={winners} />}

//       {/* Grid de Jugadores */}
//       {(ui?.uiStatus === "Countdown" || ui?.uiStatus === "Running" || status === "Running") && (
//         <div className="RojitoGame__Grid">
//           {zonesSnap.map((z) => {
//             const slug = slugify(z.zone)
//             const visualState = zonesVisual[slug] || initZoneState()
            
//             // Calculamos el porcentaje visual (0.0 a 1.0)
//             const rawProgress = target > 0 ? clamp01(visualState.tapsAccum / target) : 0
//             // Si estamos en "Burst" (recién completado), forzamos visualización llena
//             const effectiveProgress = (visualState.pumpBurstUntil > Date.now()) ? 1 : rawProgress
            
//             // Clase de color
//             const colorMod = getColorModifier(z.zone)

//             return (
//               <PlayerColumn
//                 key={slug}
//                 zoneName={z.zone}
//                 score={visualState.filledCount}
//                 scoreSlots={visualState.scoreSlots}
//                 progress={effectiveProgress}
//                 colorModifier={colorMod}
//               />
//             )
//           })}
//         </div>
//       )}
//        <p className="RojitoGame__Footer">Hecho con ♥ por Five Digital Minds | 2026</p>
//     </div>
//   )
// }

// // ───────────────────────────────────────────────
// // SUB-COMPONENTES DE PRESENTACIÓN
// // ───────────────────────────────────────────────

// /**
//  * Columna individual de un equipo/zona.
//  * Orquesta el Marcador (Arriba) y la Lata (Abajo).
//  */
// const PlayerColumn = ({ zoneName, score, scoreSlots, progress, colorModifier }) => {
//   return (
//     <div className="RojitoGame__Column">
//       {/* Parte Superior: Marcador de Latas acumuladas */}
//       <ScoreGrid zoneName={zoneName} score={score} slots={scoreSlots} colorMod={colorModifier} />
      
//       {/* Parte Inferior: La lata llenándose */}
//       <SodaFiller progress={progress} colorMod={colorModifier} />
//     </div>
//   )
// }

// /**
//  * Muestra los iconos de latas acumuladas y el contador numérico.
//  */
// function ScoreGrid({ zoneName, score, slots, colorMod }) {
//   return (
//     <div className="RojitoGame__ScoreBoard">
//       <div className="RojitoGame__ScoreBoard__Icons">
//         {slots.map((slot, i) => (
//           <div key={i + "-" + slot.pulseKey} className={`RojitoGame__ScoreBoard__Slot ${slot.active ? 'is-active' : ''}`}>
//              {/* Icono de lata pequeña para el marcador */}
//              <div className={`RojitoGame__MiniCan ${colorMod}`}></div>
//           </div>
//         ))}
//       </div>
      
//       <div className="RojitoGame__ScoreBoard__Text">
//         <div className="RojitoGame__ScoreBoard__ZoneName">{zoneName}</div>
//         <div className="RojitoGame__ScoreBoard__Count">{score}</div>
//       </div>
//     </div>
//   )
// }

// /**
//  * Componente Visual V1: Lata llenándose de líquido.
//  * Usa CSS Variables para el performance de la animación de altura.
//  */
// function SodaFiller({ progress, colorMod }) {
//   // Memoizamos el estilo para evitar reflows innecesarios en React
//   const cssVars = useMemo(() => ({ "--fill-level": `${progress * 100}%` }), [progress])

//   return (
//     <div className="RojitoGame__SodaFiller">
//       <div className="RojitoGame__SodaFiller__Container">
        
//         {/* 1. EL LÍQUIDO (Fondo) */}
//         {/* Crece de abajo hacia arriba basado en --fill-level */}
//         <div className={`RojitoGame__SodaFiller__Liquid ${colorMod}`} style={cssVars}>
//            {/* Efecto de ola en la superficie del líquido */}
//            <div className="RojitoGame__SodaFiller__Wave"></div>
//            {/* Burbujas decorativas */}
//            <div className="RojitoGame__SodaFiller__Bubbles">
//               <span></span><span></span><span></span>
//            </div>
//         </div>

//         {/* 2. LA LATA (Frente / Overlay) */}
//         {/* Imagen PNG con transparencia donde va el líquido */}
//         <div className="RojitoGame__SodaFiller__CanOverlay">
//            {/* IMPORTANTE: Esta imagen debe tener el interior transparente */}
//            <img src="/img/games/balloons/male/january-31-2026/Interaktia-Dinamicas-Digitales-Eventos-Deportivos-Rojito-Lata.png" alt="Soda Can" />
//         </div>

//       </div>
//     </div>
//   )
// }

// // ───────────────────────────────────────────────
// // BANNER DE GANADORES (Reutilizado)
// // ───────────────────────────────────────────────
// function WinnersBanner({ winners }) {
//   const type = winners?.winnerType
//   if (!type) return null
  
//   // Lógica simple de visualización de texto
//   const texts = {
//       single:  ["La zona ganadora es...", `¡${(winners?.winners?.[0] || "").toUpperCase()}!`],
//       tie:     ["Ha habido un empate entre las zonas", (winners?.winners?.join(", ") || "").toUpperCase()],
//       "all-tie": ["¡Han empatado todas las zonas!"]
//   }
  
//   const [line1, line2] = texts[type] || []
//   if (!line1) return null

//   return (
//       <div className="RojitoGame__WinnerBanner">
//           <p className="RojitoGame__WinnerBanner__Title">{line1}</p>
//           {line2 && <p className="RojitoGame__WinnerBanner__Subtitle">{line2}</p>}
//       </div>
//   )
// }

// export { Rojito };









// import { memo, useEffect, useMemo, useRef, useState } from "react"

// // ───────────────────────────────────────────────
// // Utils
// // ───────────────────────────────────────────────
// const clamp01 = (n) => Math.max(0, Math.min(1, n))
// const slugify = (s) => (s || "").toString().trim().toLowerCase()

// // Mapea zona → color. AJUSTA a tus slugs/nombres reales.
// const COLOR_BY_SLUG = {
//   azul: "BlueAdsl",
//   oro: "GoldenAdsl",
//   moctezuma: "GoldenAdsl",
//   nissan: "WhiteAdsl",
//   mobil: "GoldenAdsl",
//   tecate: "RedAdsl",
//   "laboratorio tequis": "RedAdsl",
//   "seguros el potosi": "GoldenAdsl",
//   "red cola": "RedAdsl",
//   canels: "BlueAdsl",
//   "lounge ron potosi": "GoldenAdsl",
// }

// const getColorToken = (zoneNameOrSlug) =>
//   COLOR_BY_SLUG[slugify(zoneNameOrSlug)] || "RedAdsl"

// // Clases BEM por bloque
// const colorClassFor = (scope /* 'pump' | 'box' | 'float' */, zone) => {
//   const token = getColorToken(zone)
//   if (scope === "pump")  return `Balloons__Screen__Brands--RojitoDrinks__AirPump__Balloons__Balloon--${token}`
//   if (scope === "box")   return `Balloons__Screen__Brands--RojitoDrinks__Box__Balloons__Slot__Balloon--${token}`
//   return `Balloons__Screen__Brands--RojitoDrinks__Players__Player__FloatBallon__FilledBalloon__Balloon--${token}`
// }

// // ───────────────────────────────────────────────
// // Estado visual por zona
// // ───────────────────────────────────────────────
// function initZoneState() {
//   return {
//     tapsAccum: 0,              // remainder actual (0..target-1)
//     filledBalloons: 0,         // contador total visibles
//     boxSlots: [
//       { visible: false, pulseKey: 0 },
//       { visible: false, pulseKey: 0 },
//       { visible: false, pulseKey: 0 },
//     ],
//     nextBoxIndex: 0,           // 0..2
//     floatingQueue: [],         // [{ id }]
//     pumpBurstUntil: 0,         // timestamp ms para “llenado” perceptible
//   }
// }

// const Rojito = ({ state, ui }) => {
//   // Snapshot autoritativo que manda el padre (Screen)
//   const match = state ?? {
//     matchId: null,
//     status: "Waiting",
//     target: 0,
//     zones: [],
//     winners: null
//   }

//   const matchId = match.matchId
//   const target  = match.target || 0
//   const status  = match.status || "Waiting"
//   const winners = match.winners
//   const zonesSnap = Array.isArray(match.zones) ? match.zones : []

//   // Diccionario visual: slug -> ZoneVisualState
//   const [zonesVisual, setZonesVisual] = useState({})
//   const idSeq = useRef(1)                           // IDs para globos flotantes
//   const lastServerCompletedRef = useRef(new Map())  // slug -> último completed del servidor

//   // RESET COMPLETO cuando cambia de partida
//   useEffect(() => {
//     if (!matchId) return
//     setZonesVisual({})
//     lastServerCompletedRef.current = new Map()
//   }, [matchId])

//   // Ticking ligero para apagar el "burst" (inflado perceptible) sin depender de nuevos snapshots
//   const [, forceUiTick] = useState(0)
//   const scheduleUiTick = (ms) => {
//     setTimeout(() => forceUiTick((x) => x + 1), ms)
//   }

//   // DEPURACIÓN: imprime target y taps por zona (colocado aquí)
//   useEffect(() => {
//     // Revisa en consola: target, taps y completed de cada zona
//     // Si ves target=10 siempre, el origen viene del servidor (revisa handlers del padre).
//     // eslint-disable-next-line no-console
//     console.log("[Balloons] target:", target, "zones:", zonesSnap.map(z => ({
//       zone: z.zone,
//       taps: z.currentBalloonTaps,
//       completed: z.completedBalloons
//     })))
//   }, [zonesSnap, target])

//   // Helpers inmutables
//   const ensureZone = (slug, dict) => {
//     if (!dict[slug]) dict[slug] = initZoneState()
//   }

//   const addCompletedToZone = (zone, howMany) => {
//     if (howMany <= 0) return { ...zone }

//     // 1) contador
//     let filledBalloons = zone.filledBalloons + howMany

//     // 2) flotantes (inmutable)
//     let floatingQueue = zone.floatingQueue
//     for (let i = 0; i < howMany; i++) {
//       floatingQueue = [...floatingQueue, { id: idSeq.current++ }]
//     }

//     // 3) caja (3 slots carrusel), inmutable
//     let boxSlots = zone.boxSlots.map(s => ({ ...s }))
//     let nextBoxIndex = zone.nextBoxIndex
//     for (let i = 0; i < howMany; i++) {
//       const idx = nextBoxIndex % 3
//       boxSlots[idx].visible = true
//       boxSlots[idx].pulseKey = (boxSlots[idx].pulseKey ?? 0) + 1
//       nextBoxIndex = (nextBoxIndex + 1) % 3
//     }

//     // 4) “burst” de inflado perceptible (180ms a full)
//     const pumpBurstUntil = Date.now() + 180
//     scheduleUiTick(190)

//     return {
//       ...zone,
//       filledBalloons,
//       floatingQueue,
//       boxSlots,
//       nextBoxIndex,
//       pumpBurstUntil,
//     }
//   }

//   // Reconciliar visual cuando cambie el snapshot o el target
//   useEffect(() => {
//     if (!zonesSnap.length) return

//     setZonesVisual(prev => {
//       let next = { ...prev }

//       for (const z of zonesSnap) {
//         const slug = slugify(z.zone)
//         ensureZone(slug, next)
//         const current = next[slug]

//         const serverCompleted = Math.max(0, z.completedBalloons || 0)
//         const serverRemainder = Math.max(0, z.currentBalloonTaps || 0)

//         // DIF basado en el último valor del SERVIDOR (idempotente)
//         const lastSeen = lastServerCompletedRef.current.get(slug) ?? 0
//         const completedDiff = serverCompleted - lastSeen

//         let updated = current

//         if (completedDiff > 0) {
//           updated = addCompletedToZone(updated, completedDiff)
//         }

//         // Actualiza marcador del servidor (MUY IMPORTANTE para evitar duplicados)
//         lastServerCompletedRef.current.set(slug, serverCompleted)

//         // Inflado proporcional por remainder (si target==10 y server manda 10 de golpe, veremos el "burst")
//         let tapsAccum = 0
//         if (target > 0) {
//           const capped = Math.min(serverRemainder, target - 1)
//           tapsAccum = Math.max(0, capped)
//         }
//         if (updated.tapsAccum !== tapsAccum) {
//           updated = { ...updated, tapsAccum }
//         }

//         // Sincroniza contador visual con el servidor (por seguridad)
//         if (updated.filledBalloons < serverCompleted) {
//           updated = { ...updated, filledBalloons: serverCompleted }
//         }

//         if (updated !== current) {
//           next = { ...next, [slug]: updated }
//         }
//       }

//       // Limpia zonas que ya no vienen en el snapshot
//       const alive = new Set(zonesSnap.map(z => slugify(z.zone)))
//       for (const k of Object.keys(next)) {
//         if (!alive.has(k)) {
//           const copy = { ...next }
//           delete copy[k]
//           next = copy
//           lastServerCompletedRef.current.delete(k)
//         }
//       }

//       return next
//     })
//   }, [zonesSnap, target])

//   // Remueve flotante al terminar animación (inmutable)
//   const handleFloatingEnd = (slug, id) => {
//     setZonesVisual(prev => {
//       const zone = prev[slug]
//       if (!zone) return prev
//       return {
//         ...prev,
//         [slug]: {
//           ...zone,
//           floatingQueue: zone.floatingQueue.filter(f => f.id !== id)
//         }
//       }
//     })
//   }

//   const showFinishedBanner = status === "Finished" && winners && winners.winnerType !== "none"

//   return (
//     <div className="Balloons__Screen__Brands--RojitoDrinks">
//         <p className="Balloons__Screen__Brands--RojitoDrinks__Title"></p>

//         {showFinishedBanner && <WinnersBanner winners={winners} />}

//         {(ui?.uiStatus === "Countdown" || ui?.uiStatus === "Running" || status === "Running") && (
//             <div className="Balloons__Screen__Brands--RojitoDrinks__Players">
//                 {zonesSnap.map((z) => {
//                     const slug = slugify(z.zone)
//                     const v = zonesVisual[slug] || initZoneState()

//                     // Si hubo "burst", mostramos 1.0 por ~180ms; si no, usamos remainder/target
//                     const progressBase = target > 0 ? clamp01(v.tapsAccum / target) : 0
//                     const now = Date.now()
//                     const effectiveProgress = (v.pumpBurstUntil > now) ? 1 : progressBase

//                     // Color (bomba, caja, flotantes)
//                     const pumpColorClass  = colorClassFor("pump",  z.zone)
//                     const boxColorClass   = colorClassFor("box",   z.zone)
//                     const floatColorClass = colorClassFor("float", z.zone)

//                     return (
//                     <PlayerColumn
//                         key={slug}
//                         slug={slug}
//                         zoneName={z.zone}
//                         counter={v.filledBalloons}
//                         progress={effectiveProgress}
//                         boxSlots={v.boxSlots}
//                         floatingQueue={v.floatingQueue}
//                         pumpColorClass={pumpColorClass}
//                         boxColorClass={boxColorClass}
//                         floatColorClass={floatColorClass}
//                         onFloatingEnd={handleFloatingEnd}
//                     />
//                     )
//                 })}
//             </div>
//         )}

//         <p className="Balloons__Screen__Brands--RojitoDrinks__Footer FooterCredits">Hecho con ♥ por Five Digital Minds | 2026</p>
//     </div>
//   )
// }

// // ───────────────────────────────────────────────
// // Presentación
// // ───────────────────────────────────────────────
// function WinnersBanner({ winners }) {
//   const t = winners?.winnerType
//   if (t === "single") {
//     const z = winners?.winners?.[0] ?? ""
//     return (
//         <div className="Balloons__Screen__Brands--RojitoDrinks__Winners">
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Single'>La zona ganadora es...</p>
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Single'>¡{z.toUpperCase()}!</p>
//         </div>
//     )
//   }
//   if (t === "tie") {
//     const list = winners?.winners?.toUpperCase().join(", ") ?? ""
//     return (
//         <div className="Balloons__Screen__Brands--RojitoDrinks__Winners">
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Tie'>Ha habido un empate entre las zonas</p>
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--Tie'>{list}</p>
//         </div>
//     )
//   }
//   if (t === "all-tie") {
//     return (
//         <div className="Balloons__Screen__Brands--RojitoDrinks__Winners">
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Winners__Text--AllTie'>¡Han empatado todas las zonas!</p>
//         </div>
//     )
//   }
//   return null
// }

// function PlayerColumn({
//     slug,
//     zoneName,
//     counter,
//     progress,
//     boxSlots,
//     floatingQueue,
//     pumpColorClass,
//     boxColorClass,
//     floatColorClass,
//     onFloatingEnd
// }) {
//   return (
//     <div className="Balloons__Screen__Brands--RojitoDrinks__Players__Player">
//         <Box zoneName={zoneName} counter={counter} boxSlots={boxSlots} boxColorClass={boxColorClass} />
//         <AirPump progress={progress} pumpColorClass={pumpColorClass} />
//         {/* <FloatBalloonsRenderer
//             slug={slug}
//             floatingQueue={floatingQueue}
//             floatColorClass={floatColorClass}
//             onEnd={onFloatingEnd}
//         /> */}
//     </div>
//   )
// }

// function Box({ zoneName, counter, boxSlots, boxColorClass }) {
//   return (
//     <div className='Balloons__Screen__Brands--RojitoDrinks__Box'>
//         <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Balloons'>
//             <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Balloons__Slot'>
//                 {boxSlots.map((s, i) => (
//                     <div
//                         key={i + "-" + s.pulseKey}
//                         className={[
//                             "Balloons__Screen__Brands--RojitoDrinks__Box__Balloons__Slot__Balloon",
//                             boxColorClass,
//                             s.visible ? "is-visible" : "is-hidden"
//                         ].join(" ")}
//                     />
//                 ))}
//             </div>
//         </div>
//         <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Box' />
//         <div className='Balloons__Screen__Brands--RojitoDrinks__Box__Counter'>
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Box__Counter__Text'>{zoneName.toUpperCase()}</p>
//             <p className='Balloons__Screen__Brands--RojitoDrinks__Box__Counter__Text'>{counter}</p>
//         </div>
//     </div>
//   )
// }

// function AirPump({ progress, pumpColorClass }) {
//   // para que React NO re-pinte el nodo cada frame, memoizamos el estilo
//   const styleVar = useMemo(() => ({ "--p": Number.isFinite(progress) ? progress : 0 }), [progress])
//   return (
//     <div className='Balloons__Screen__Brands--RojitoDrinks__AirPump'>
//         <div className='Balloons__Screen__Brands--RojitoDrinks__AirPump__Balloons'>
//             <div className={["Balloons__Screen__Brands--RojitoDrinks__AirPump__Balloons__Balloon", pumpColorClass].join(" ")} style={styleVar}/>
//         </div>
//         <div className='Balloons__Screen__Brands--RojitoDrinks__AirPump__Image'>
//             <img src='/img/games/balloons/inflate-balloons.gif' alt='Bomba' />
//         </div>
//     </div>
//   )
// }

// // Componente de globo flotante que NO se remonta al re-render del padre
// const AnimatedBalloon = memo(function AnimatedBalloon({ className, onEnd }) {
//     // No depende de props cambiantes; el nodo permanece y su animación CSS no se reinicia
//     return <div className={className} onAnimationEnd={onEnd} />
// })

// function FloatBalloonsRenderer({ slug, floatingQueue, floatColorClass, onEnd }) {
//     return (
//         <div className='Balloons__Screen__Brands--RojitoDrinks__Players__Player__FloatBallon'>
//             <div className='Balloons__Screen__Brands--RojitoDrinks__Players__Player__FloatBallon__FilledBalloon'>
//                 {floatingQueue.map(f => (
//                     <AnimatedBalloon
//                         key={f.id}
//                         className={[
//                         "Balloons__Screen__Brands--RojitoDrinks__Players__Player__FloatBallon__FilledBalloon__Balloon",
//                         floatColorClass,
//                         "is-floating"
//                         ].join(" ")}
//                         onEnd={() => onEnd?.(slug, f.id)}
//                     />
//                 ))}
//             </div>
//         </div>
//     )
// }

// export { Rojito };